<template>
  <div class="cms-form-group" :class="{ 'cms-form-group-full': fullWidth, 'cms-disabled': disabled }">
    <label v-if="label">{{ label }}</label>
    <div class="cms-editor-wrapper">
      <editor
        :model-value="modelValue"
        :init="editorConfig"
        :plugins="plugins"
        :toolbar="toolbar"
        :disabled="disabled"
        @update:modelValue="$emit('update:modelValue', $event)"
      />
    </div>
    <!-- <span v-if="helpText" class="cms-form-help">{{ helpText }}</span> -->
  </div>
</template>

<script lang="ts" setup>
import Editor from '@tinymce/tinymce-vue';
import { computed, ref } from 'vue';
import api from '@/api';

interface Props {
  modelValue: string;
  label?: string;
  fullWidth?: boolean;
  helpText?: string;
  height?: number;
  plugins?: string;
  toolbar?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: true,
  height: 360,
  plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table code help wordcount',
  toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | link | image | code | help',
  disabled: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'mediaUploaded', media: { uuid: string; src: string; name: string }): void;
}>();

const editorInstance = ref<any>(null);

const createImageUploadHandler = () => {
  return (blobInfo: any) =>
    new Promise(async (resolve: any, reject: any) => {
      try {
        const formData = new FormData();
        formData.append('file', blobInfo.blob(), blobInfo.filename());

        const media: any = await api.media.upload('s3', formData, {
          'Content-Type': 'multipart/form-data',
        });

        const mediaObject = {
          uuid: media.uuid,
          src: media.url,
          name: blobInfo.name(),
        };

        // Emit event for parent component
        emit('mediaUploaded', mediaObject);

        if (!!media.url) {
          resolve(media.url);
        } else {
          reject('Failed to upload image');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        reject('Failed to upload image');
      }
    });
};

const editorConfig = computed(() => ({
  height: props.height,
  menubar: false,
  plugins: props.plugins,
  toolbar: props.toolbar,
  readonly: props.disabled ? 1 : 0,
  automatic_uploads: true,
  images_upload_handler: createImageUploadHandler(),
  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
  setup: (editor: any) => {
    editor.on('init', () => {
      editorInstance.value = editor;
    });
  },
}));

function insertAtCursor(text: string) {
  if (!editorInstance.value) return false;
  editorInstance.value.focus();
  editorInstance.value.insertContent(text);
  return true;
}

defineExpose({
  insertAtCursor,
});
</script>

<!-- 
  Styles: Uses global .cms-form-group and .cms-editor-wrapper classes from cms-styles.scss
  No component-specific styles needed
-->



